import { supabase } from './supabase';

export interface CreditDiscrepancy {
  userId: string;
  userName: string;
  userEmail: string;
  actualBalance: number;
  calculatedBalance: number;
  difference: number;
  missingTransactions: Array<{
    type: string;
    expectedAmount: number;
    reason: string;
  }>;
}

export interface ReconciliationReport {
  timestamp: string;
  totalUsersAudited: number;
  usersWithDiscrepancies: number;
  totalCreditsDiscrepancy: number;
  discrepancies: CreditDiscrepancy[];
}

export const creditsReconciliationService = {
  /**
   * Audit all user credits and identify discrepancies
   */
  async auditAllUserCredits(): Promise<ReconciliationReport> {
    console.log('[Credits Reconciliation] Starting full audit...');

    const { data: users, error: usersError } = await supabase
      .from('profiles')
      .select('id, full_name, email');

    if (usersError || !users) {
      throw new Error('Failed to fetch users for audit');
    }

    const discrepancies: CreditDiscrepancy[] = [];
    let totalDiscrepancy = 0;

    for (const user of users) {
      const discrepancy = await this.auditUserCredits(user.id);
      if (discrepancy && discrepancy.difference !== 0) {
        discrepancies.push({
          ...discrepancy,
          userName: user.full_name,
          userEmail: user.email,
        });
        totalDiscrepancy += Math.abs(discrepancy.difference);
      }
    }

    const report: ReconciliationReport = {
      timestamp: new Date().toISOString(),
      totalUsersAudited: users.length,
      usersWithDiscrepancies: discrepancies.length,
      totalCreditsDiscrepancy: totalDiscrepancy,
      discrepancies,
    };

    console.log('[Credits Reconciliation] Audit complete:', {
      totalUsers: users.length,
      discrepancies: discrepancies.length,
    });

    return report;
  },

  /**
   * Audit a single user's credits
   */
  async auditUserCredits(userId: string): Promise<CreditDiscrepancy | null> {
    const { data: credits } = await supabase
      .from('credits')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (!credits) {
      return null;
    }

    const { data: transactions } = await supabase
      .from('credit_transactions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });

    let calculatedBalance = 0;
    for (const transaction of transactions || []) {
      calculatedBalance += transaction.amount;
    }

    const missingTransactions = await this.findMissingTransactions(userId);

    return {
      userId,
      userName: '',
      userEmail: '',
      actualBalance: credits.balance,
      calculatedBalance,
      difference: credits.balance - calculatedBalance,
      missingTransactions,
    };
  },

  /**
   * Find missing credit transactions for a user
   */
  async findMissingTransactions(userId: string): Promise<Array<{
    type: string;
    expectedAmount: number;
    reason: string;
  }>> {
    const missing: Array<{
      type: string;
      expectedAmount: number;
      reason: string;
    }> = [];

    const { data: transactions } = await supabase
      .from('credit_transactions')
      .select('type')
      .eq('user_id', userId);

    const transactionTypes = new Set(transactions?.map(t => t.type) || []);

    const { data: swipes } = await supabase
      .from('swipes')
      .select('*')
      .eq('user_id', userId)
      .eq('direction', 'right');

    if (swipes) {
      const swipeRightTransactions = transactions?.filter(t => t.type === 'swipe_right') || [];
      const expectedSwipeCredits = swipes.length * 1;
      const actualSwipeCredits = swipeRightTransactions.length * 1;

      if (actualSwipeCredits < expectedSwipeCredits) {
        missing.push({
          type: 'swipe_right',
          expectedAmount: expectedSwipeCredits - actualSwipeCredits,
          reason: `Missing ${swipes.length - swipeRightTransactions.length} swipe right credits`,
        });
      }
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('account_complete')
      .eq('id', userId)
      .maybeSingle();

    if (profile?.account_complete && !transactionTypes.has('profile_complete')) {
      missing.push({
        type: 'profile_complete',
        expectedAmount: 10,
        reason: 'Profile completed but credit not awarded',
      });
    }

    return missing;
  },

  /**
   * Fix credits for a user by awarding missing credits
   */
  async fixUserCredits(userId: string, adminId: string): Promise<{
    success: boolean;
    creditsAwarded: number;
    transactionsCreated: number;
  }> {
    const discrepancy = await this.auditUserCredits(userId);

    if (!discrepancy || discrepancy.missingTransactions.length === 0) {
      return {
        success: true,
        creditsAwarded: 0,
        transactionsCreated: 0,
      };
    }

    let totalCreditsAwarded = 0;
    let transactionsCreated = 0;

    for (const missing of discrepancy.missingTransactions) {
      try {
        await supabase.from('credit_transactions').insert({
          user_id: userId,
          amount: missing.expectedAmount,
          type: `${missing.type}_reconciliation`,
          description: `Reconciliation: ${missing.reason}`,
        });

        const { data: currentCredits } = await supabase
          .from('credits')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (currentCredits) {
          await supabase
            .from('credits')
            .update({
              balance: currentCredits.balance + missing.expectedAmount,
              total_earned: currentCredits.total_earned + missing.expectedAmount,
              updated_at: new Date().toISOString(),
            })
            .eq('user_id', userId);
        }

        totalCreditsAwarded += missing.expectedAmount;
        transactionsCreated++;
      } catch (error) {
        console.error('[Credits Reconciliation] Error fixing credits:', error);
      }
    }

    await supabase.from('admin_actions').insert({
      admin_id: adminId,
      action_type: 'credits_reconciliation',
      target_type: 'user',
      target_id: userId,
      details: {
        creditsAwarded: totalCreditsAwarded,
        transactionsCreated,
        missingTransactions: discrepancy.missingTransactions,
      },
    });

    return {
      success: true,
      creditsAwarded: totalCreditsAwarded,
      transactionsCreated,
    };
  },

  /**
   * Fix credits for all users with discrepancies
   */
  async fixAllUserCredits(adminId: string): Promise<{
    success: boolean;
    usersFixed: number;
    totalCreditsAwarded: number;
  }> {
    const report = await this.auditAllUserCredits();

    let usersFixed = 0;
    let totalCreditsAwarded = 0;

    for (const discrepancy of report.discrepancies) {
      if (discrepancy.missingTransactions.length > 0) {
        const result = await this.fixUserCredits(discrepancy.userId, adminId);
        if (result.success) {
          usersFixed++;
          totalCreditsAwarded += result.creditsAwarded;
        }
      }
    }

    return {
      success: true,
      usersFixed,
      totalCreditsAwarded,
    };
  },

  /**
   * Generate a detailed reconciliation report for export
   */
  async generateDetailedReport(): Promise<string> {
    const report = await this.auditAllUserCredits();

    let reportText = `Credits Reconciliation Report\n`;
    reportText += `Generated: ${new Date(report.timestamp).toLocaleString()}\n`;
    reportText += `Total Users Audited: ${report.totalUsersAudited}\n`;
    reportText += `Users with Discrepancies: ${report.usersWithDiscrepancies}\n`;
    reportText += `Total Credits Discrepancy: ${report.totalCreditsDiscrepancy}\n\n`;

    if (report.discrepancies.length > 0) {
      reportText += `Discrepancies:\n`;
      reportText += `${'='.repeat(80)}\n\n`;

      for (const disc of report.discrepancies) {
        reportText += `User: ${disc.userName} (${disc.userEmail})\n`;
        reportText += `User ID: ${disc.userId}\n`;
        reportText += `Actual Balance: ${disc.actualBalance}\n`;
        reportText += `Calculated Balance: ${disc.calculatedBalance}\n`;
        reportText += `Difference: ${disc.difference}\n`;

        if (disc.missingTransactions.length > 0) {
          reportText += `Missing Transactions:\n`;
          for (const missing of disc.missingTransactions) {
            reportText += `  - ${missing.type}: ${missing.expectedAmount} credits (${missing.reason})\n`;
          }
        }
        reportText += `\n${'-'.repeat(80)}\n\n`;
      }
    } else {
      reportText += `No discrepancies found. All user credits are accurate.\n`;
    }

    return reportText;
  },
};
